import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import {
  Alert,
  Badge,
  Breadcrumbs,
  Button,
  Card,
  FileInput,
  Group,
  List,
  Table,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import type { FormTemplateSummary } from '@isg/shared-types';
import type { FormSemaTaslak, FormTemplateExcelIceAktarSonucu } from '@isg/validation';
import {
  useCompanies,
  useFormSablonAd,
  useFormSablonAksiyonKaynagi,
  useFormSablonBosTuval,
  useFormSablonExcelYukle,
  useFormSablon,
  useFormSablonKapsam,
  useFormSablonKapsamSecenekleri,
  useFormSurum,
  useFormSurumGecmisi,
  useFormSurumAc,
  useFormSurumGuncelle,
  useFormSurumYayinla,
  useLookup,
  useWorkplacesTumu,
} from '../api/queries';
import { KapsamSecici, SablonDuzenleyici, taslakSemayiCoz } from './form-parts';
import { QueryStatus } from './parts';
import { tarihGoster } from '../lib/format';
import { ANLAM } from '../theme';

/**
 * ŞABLON SAYFASI — Ş7 + madde 143 + madde 144 (Tur 3).
 *
 * ⚠️ **İKİ EKRAN BİRLEŞTİ (143).** Önce *"yeni şablon"* penceresi, sonra
 * şablon penceresi açılıyordu ve ilk penceredeki her şey (ad · kapsam ·
 * aksiyon kaynağı) ikincisinde de yazılabiliyordu — yani ilk pencere
 * **gereksiz bir adımdı.** Artık tek sayfa var: `/formlar/sablonlar/yeni`
 * künyeyi boş açar, kaydedince aynı sayfanın düzenleme hâline geçer.
 *
 * ⚠️ **SAYFA GENİŞ (144).** Hedef *"geniş bir Excel sayfası"* hissidir: iki
 * soruluk formda fark etmez, ama saha denetimi ve periyodik kontrol
 * checklistleri **onlarca maddelik** olacak ve art arda giriş ritmi (P1-b)
 * dar bir modalda kırılır.
 *
 * ⚠️ **HER DEĞİŞİKLİK ANINDA YAZILIR** (işlevsel düzeltme turunun kuralı):
 * ara *"taslağı kaydet"* düğmesi YOKTUR; taslak sürüm yoksa düzenleyici
 * SALT OKUNUR çizilir ve yolu *"Düzenlemeye başla"* açar.
 */
export function SablonDuzenleScreen(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  return id === 'yeni' ? <YeniSablon /> : <MevcutSablon templateId={id!} />;
}

/**
 * KAPSAM ETİKETİ (Y5) — *"matbu"* / *"merkezî"* / *"Holding"* kavramları
 * KALKTI (T1). Kullanıcı bir DÜZEY değil bir İŞYERİ KÜMESİ okur; ekran
 * ikinci bir kural türetmez, sunucunun verdiği üç alanı okur.
 */
function kapsamEtiketi(sablon: FormTemplateSummary): string {
  if (sablon.kapsamIsyeriSayisi === 0) return 'Kapsam yok';
  if (sablon.kapsamTumIsyerleri) return `Tüm işyerleri (${sablon.kapsamIsyeriSayisi})`;
  if (sablon.kapsamIsyeriSayisi <= sablon.kapsamAdlari.length) {
    return sablon.kapsamAdlari.join(', ');
  }
  return `${sablon.kapsamAdlari.join(', ')} +${sablon.kapsamIsyeriSayisi - sablon.kapsamAdlari.length}`;
}

/**
 * YENİ ŞABLONUN KAPSAM HAVUZU. ⚠️ Şablon henüz YOK, dolayısıyla
 * `scope-options` ucu çağrılamaz; havuz kullanıcının kendi bahçesindeki
 * işyerleridir ve **aynı uçtan** (`workplaces/tumu`) gelir — sunucu zaten
 * bahçeyi uygular, ekran ikinci filtre yazmaz (§2.16/B3).
 */
function useYeniSablonKapsamSecenekleri(): {
  data: { isyerleri: { id: string; ad: string; companyAd: string }[] } | undefined;
} {
  // K12 — SEÇİCİ arşivli işyerini göstermez: doldurulamayacak bir kapsam
  // çizmek §2.40'ın "görünür, çalışmaz" sınıfıdır.
  const isyerleri = useWorkplacesTumu({ durum: 'aktif' });
  const sirketler = useCompanies();
  if (!isyerleri.data || !sirketler.data) return { data: undefined };
  const adlar = new Map(sirketler.data.map((c) => [c.id, c.ad]));
  return {
    data: {
      isyerleri: isyerleri.data.map((w) => ({
        id: w.id,
        ad: w.ad,
        companyAd: adlar.get(w.companyId) ?? '—',
      })),
    },
  };
}

/** Künye satırı — üç sayfa da (yeni · mevcut · salt okunur) aynı başlığı taşır. */
function SayfaBasligi({ ad }: { ad: string }): React.JSX.Element {
  return (
    <Breadcrumbs data-testid="sablon-gezinti">
      <Link to="/formlar">Formlar</Link>
      <Text>{ad}</Text>
    </Breadcrumbs>
  );
}

/**
 * BOŞ TUVAL — Ş2'nin kaçış kapısı. **İKİ SORU: ad + kapsam** (G2); aksiyon
 * kaynağı şablon açıldıktan sonra KÜNYEDE sorulur. Kaydedilince sayfa
 * düzenleme hâline geçer (143: ayrı bir ön pencere yok).
 */
function YeniSablon(): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  // ✏️ P1 (Namet grubu, 01.11.2026): AYNI SAYFANIN EXCEL KİPİ — Formlar
  // listesindeki "Excel'den yükle" buraya `state.excel` ile gelir. Sayfa yeni
  // bir ekran DEĞİLDİR (önizleme yok): boş tuvalin iki sorusuna (ad + kapsam)
  // bir dosya alanı eklenir; ad dosya adından ön dolar, kullanıcı değiştirir.
  // Kapsam burada sorulur çünkü kapsamsız şablon ölü doğar (§22.5c — T5) ve
  // dar bahçeli açana bile görünmez; iş emrinin "kapsam boş kalır" satırından
  // gerekçeli sapma (sunucu şeması da boş kapsamı reddeder).
  const excelKipi = Boolean((location.state as { excel?: boolean } | null)?.excel);
  // ✏️ F4·S6 (T2/T6): kapsam artık ŞİRKET değil İŞYERİ KÜMESİDİR ve havuz
  // SUNUCUDAN gelir (bahçedeki işyerleri). İşyeri bahçeli uzman da şablon
  // açabilir — eski 422 kalktı (T6).
  const havuz = useYeniSablonKapsamSecenekleri();
  const bosTuval = useFormSablonBosTuval();
  const excelYukle = useFormSablonExcelYukle();
  const [ad, setAd] = useState('');
  const [kapsam, setKapsam] = useState<string[]>([]);
  const [dosya, setDosya] = useState<File | null>(null);
  const bekliyor = bosTuval.isPending || excelYukle.isPending;
  const hata = bosTuval.error ?? excelYukle.error;
  const engel =
    ad.trim().length === 0
      ? 'Şablon adı girin'
      : kapsam.length === 0
        ? 'En az bir işyeri seçin'
        : excelKipi && dosya === null
          ? 'Excel dosyasını seçin'
          : null;

  const kaydet = (): void => {
    if (excelKipi) {
      if (!dosya) return;
      excelYukle.mutate(
        { dosya, ad: ad.trim(), kapsamIsyeriIdleri: kapsam },
        {
          onSuccess: (sonuc) =>
            void navigate(`/formlar/sablonlar/${sonuc.sablon.id}`, {
              replace: true,
              state: { iceAktarma: sonuc },
            }),
        },
      );
      return;
    }
    bosTuval.mutate(
      { ad: ad.trim(), kapsamIsyeriIdleri: kapsam, aksiyonKaynagiId: null },
      {
        onSuccess: (sablon: FormTemplateSummary) =>
          void navigate(`/formlar/sablonlar/${sablon.id}`, { replace: true }),
      },
    );
  };

  return (
    <Stack data-testid="sablon-yeni-sayfa" data-kip={excelKipi ? 'excel' : 'bos-tuval'}>
      <SayfaBasligi ad="Yeni şablon" />
      <Title order={3}>{excelKipi ? "Excel'den form şablonu" : 'Yeni form şablonu'}</Title>
      <Card withBorder maw={720}>
        <Stack>
          {excelKipi && (
            <FileInput
              label="Excel dosyası"
              required
              accept=".xlsx"
              placeholder="İndirdiğiniz şablonun doldurulmuş hâli (.xlsx)"
              value={dosya}
              onChange={(secilen) => {
                setDosya(secilen);
                excelYukle.reset();
                // Şablon adı DOSYA ADINDAN (uzantısız) — kullanıcı yazmışsa dokunulmaz.
                if (secilen && ad.trim().length === 0) {
                  setAd(secilen.name.replace(/\.[^.]+$/, '').trim());
                }
              }}
              clearable
              data-testid="bahceli-sablon-excel-dosya"
            />
          )}
          <TextInput
            label="Şablon adı"
            required
            value={ad}
            onChange={(event) => setAd(event.currentTarget.value)}
            data-testid="bahceli-sablon-ad"
          />
          <KapsamSecici
            isyerleri={havuz.data?.isyerleri ?? []}
            secili={kapsam}
            onDegisti={setKapsam}
            testId="bahceli-sablon-kapsam"
          />
          {/* ⚠️ **G2 — AÇILIŞ EKRANI İKİ SORUYA İNDİ (ad + kapsam).** Aksiyon
              kaynağı burada da sorulup künyede DE düzenlenebiliyordu; şablonu
              daha kurmadan *"bu formdan aksiyon açılırsa kaynağı ne olsun"*
              sorusuna cevap vermek, kullanıcıyı henüz görmediği bir akış
              hakkında karar vermeye zorluyordu. Alan **künyede yaşamaya devam
              eder** (42.8 — tek kaynak kolon; kaldırılan yalnız ERKEN soru). */}
          {hata && (
            <Alert color={ANLAM.engel} role="alert" data-testid="bahceli-sablon-hata">
              {hata.message}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button variant="default" component={Link} to="/formlar">
              Vazgeç
            </Button>
            {/* ⚠️ PASİF DÜĞMENİN SEBEBİ YANINDA YAZAR (§2.40 / G5) — sayfanın
                altında değil. */}
            {engel && (
              <Text size="xs" c="dimmed" data-testid="bahceli-sablon-engel">
                {engel}
              </Text>
            )}
            <Button
              disabled={engel !== null || bekliyor}
              loading={bekliyor}
              data-testid="bahceli-sablon-kaydet"
              onClick={kaydet}
            >
              {excelKipi ? "Excel'den aç" : 'Şablonu aç'}
            </Button>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}

/**
 * ✏️ P1: Excel'den açılan şablonun bandı — yükleme yanıtı yönlendirici
 * durumuyla gelir (önbelleğe girmez, yenilemede düşer; tek seferlik bilgi).
 * Tanınmayan değer varsa DİKKAT bandı satır·alan·değer listesiyle ("N satırda
 * tanınmayan değer var, boş bırakıldı — tamamlayın."); yoksa bilgi bandı.
 */
function IceAktarmaBandi({ sonuc }: { sonuc: FormTemplateExcelIceAktarSonucu }): React.JSX.Element {
  const satirSayisi = new Set(sonuc.uyarilar.map((u) => u.satir)).size;
  if (sonuc.uyarilar.length === 0) {
    return (
      <Alert color={ANLAM.bilgi} data-testid="sablon-ice-aktarma-bilgi">
        Excel'den {sonuc.maddeSayisi} madde, {sonuc.bolumSayisi} bölüm alındı — taslak v1.
        Düzenleyip yayınlayın.
      </Alert>
    );
  }
  return (
    <Alert color={ANLAM.dikkat} role="alert" data-testid="sablon-ice-aktarma-uyari">
      <Stack gap={4}>
        <Text size="sm" fw={500}>
          {satirSayisi} satırda tanınmayan değer var, boş bırakıldı — tamamlayın.
        </Text>
        <List size="sm" data-testid="sablon-ice-aktarma-uyari-listesi">
          {sonuc.uyarilar.map((u, i) => (
            <List.Item key={i}>
              Satır {u.satir} · {u.alan}: &quot;{u.deger}&quot;
            </List.Item>
          ))}
        </List>
      </Stack>
    </Alert>
  );
}

function MevcutSablon({ templateId }: { templateId: string }): React.JSX.Element {
  // ✏️ F4·S6 (Y1): TEKİL UÇ — liste sayfalandığı için "listeden bul" deseni
  // sessizce yanlış cevap verirdi (tur 9 / T9-03'ün form kolu).
  const sablonlar = useFormSablon(templateId);
  const sablon = sablonlar.data ?? null;
  const location = useLocation();
  const iceAktarma =
    (location.state as { iceAktarma?: FormTemplateExcelIceAktarSonucu } | null)?.iceAktarma ?? null;

  return (
    <Stack data-testid="sablon-sayfasi">
      <SayfaBasligi ad={sablon?.ad ?? 'Şablon'} />
      {iceAktarma && iceAktarma.sablon.id === templateId && <IceAktarmaBandi sonuc={iceAktarma} />}
      <QueryStatus queries={[sablonlar]} kayit />
      {sablonlar.isSuccess &&
        (sablon === null ? (
          <Alert color={ANLAM.engel} role="alert" data-testid="sablon-bulunamadi">
            Şablon bulunamadı ya da görünürlüğünüzün dışında.
          </Alert>
        ) : (
          <SablonGovdesi sablon={sablon} />
        ))}
    </Stack>
  );
}

/**
 * SÜRÜM GEÇMİŞİ (Y4).
 *
 * ⚠️ Ekran bugüne kadar yalnız **iki rozet** gösteriyordu (yayınlı + taslak);
 * §22.5/3'ün tüm dayanağı **kaydın kendi sürümüyle okunması**dır ve o
 * sürümlerin varlığı hiçbir yerde görünmüyordu. Satır başına **o sürümle
 * doldurulmuş kayıt sayısı** yazar — kuralın görünen kanıtı.
 */
function SurumGecmisi({ sablon }: { sablon: FormTemplateSummary }): React.JSX.Element {
  const gecmis = useFormSurumGecmisi(sablon.id);
  return (
    <Card withBorder data-testid="sablon-surum-gecmisi">
      <Stack gap="xs">
        <Text fw={500} size="sm">
          Sürüm geçmişi
        </Text>
        <QueryStatus queries={[gecmis]} />
        {gecmis.isSuccess && (
          <Table withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={90}>Sürüm</Table.Th>
                <Table.Th w={120}>Durum</Table.Th>
                <Table.Th w={140}>Yayınlanma</Table.Th>
                <Table.Th w={110}>Kayıt</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {gecmis.data.map((surum) => (
                <Table.Tr key={surum.id} data-testid={`sablon-surum-${surum.surumNo}`}>
                  <Table.Td>v{surum.surumNo}</Table.Td>
                  <Table.Td>
                    <Badge
                      size="xs"
                      color={surum.yayinDurumu === 'published' ? ANLAM.olumlu : ANLAM.dikkat}
                    >
                      {surum.yayinDurumu === 'published' ? 'yayınlı' : 'taslak'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {surum.yayinlanmaAt ? tarihGoster(surum.yayinlanmaAt) : '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{surum.kayitSayisi}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </Card>
  );
}

/**
 * KAPSAM DÜZENLEME (Y2 · T2 · T3 · T4).
 *
 * ⚠️ **YENİ SÜRÜM DOĞMAZ (T3):** kapsam içerik değildir. Bu yüzden kapsam
 * kartı düzenleyicinin DIŞINDADIR ve taslak sürüm gerektirmez — *"düzenlemeye
 * başla"* akışına bağlanmaz.
 *
 * ⚠️ **KAYDET AYRI BİR EYLEMDİR** (şema düzenleyicisinin *"anında yaz"*
 * modeli burada UYGULANMAZ): kapsam bir KÜMEDİR ve her tıkta yazmak, ara
 * hâllerde şablonu geçici olarak yanlış işyerlerinde geçerli kılardı.
 * Kart, kapsamı **yazma yetkisi yoksa SALT OKUNUR** çizer (§2.40: çalışamayacak
 * kontrol gösterilmez).
 */
function KapsamKarti({ sablon }: { sablon: FormTemplateSummary }): React.JSX.Element {
  const havuz = useFormSablonKapsamSecenekleri(sablon.id);
  const kapsamYaz = useFormSablonKapsam();
  const [secili, setSecili] = useState<string[] | null>(null);
  const sunucudaki = havuz.data?.seciliIsyeriIdleri ?? [];
  const guncel = secili ?? sunucudaki;
  const degisti =
    secili !== null &&
    (secili.length !== sunucudaki.length || secili.some((id) => !sunucudaki.includes(id)));

  return (
    <Card withBorder data-testid="sablon-kapsam-karti">
      <Stack gap="xs">
        <KapsamSecici
          isyerleri={havuz.data?.isyerleri ?? []}
          secili={guncel}
          onDegisti={setSecili}
          duzenlenebilir={sablon.yetkiler.duzenleyebilir}
        />
        {sablon.yetkiler.duzenleyebilir && (
          <Group justify="flex-end" gap="xs">
            {degisti && guncel.length === 0 && (
              <Text size="xs" c="dimmed" data-testid="sablon-kapsam-engel">
                En az bir işyeri seçin
              </Text>
            )}
            <Button
              size="xs"
              variant="default"
              disabled={!degisti || guncel.length === 0 || kapsamYaz.isPending}
              data-testid="sablon-kapsam-kaydet"
              onClick={() =>
                kapsamYaz.mutate(
                  { templateId: sablon.id, kapsamIsyeriIdleri: guncel },
                  { onSuccess: () => setSecili(null) },
                )
              }
            >
              Kapsamı kaydet
            </Button>
          </Group>
        )}
        {kapsamYaz.isError && (
          <Alert color={ANLAM.engel} role="alert" data-testid="sablon-kapsam-hata">
            {kapsamYaz.error.message}
          </Alert>
        )}
      </Stack>
    </Card>
  );
}

function SablonGovdesi({ sablon }: { sablon: FormTemplateSummary }): React.JSX.Element {
  const aksiyonKaynaklari = useLookup('aksiyon_kaynagi', { enabled: !sablon.kutuphaneden });
  const adYaz = useFormSablonAd();
  const kaynakYaz = useFormSablonAksiyonKaynagi();
  const surumAc = useFormSurumAc();
  const surumGuncelle = useFormSurumGuncelle();
  const yayinla = useFormSurumYayinla();

  // Düzenleme TASLAK sürümde yapılır; taslak yoksa YAYINLI sürüm salt okunur
  // gösterilir (yayınlanmış sürüm DEĞİŞMEZ — §22.5/3).
  const gosterilenSurumId = sablon.taslakSurumId ?? sablon.yayinliSurumId;
  const surum = useFormSurum(gosterilenSurumId);
  const taslakVar = sablon.taslakSurumId !== null;
  const duzenlenebilir = taslakVar && sablon.yetkiler.duzenleyebilir;

  const [ad, setAd] = useState(sablon.ad);
  useEffect(() => setAd(sablon.ad), [sablon.ad]);

  const sema = useMemo<FormSemaTaslak | null>(
    () => taslakSemayiCoz(surum.data?.sema),
    [surum.data],
  );
  const bosSablon = (sema?.alanlar.length ?? 0) === 0;

  return (
    <Stack>
      <Group justify="space-between" align="flex-start">
        <Group gap="xs" align="baseline">
          <Title order={3}>{sablon.ad}</Title>
          <Badge size="sm" variant="light" data-testid="sablon-surum-rozeti">
            {sablon.yayinliSurumNo ? `v${sablon.yayinliSurumNo}` : 'yayınsız'}
          </Badge>
          {taslakVar && (
            <Badge size="sm" color={ANLAM.dikkat} data-testid="sablon-taslak-rozeti">
              taslak v{sablon.taslakSurumNo}
            </Badge>
          )}
        </Group>
        <Group gap="xs">
          {/* ⚠️ **G4 — "Düzenlemeye başla" DÜĞMESİ BANDIN İÇİNE TAŞINDI**
              (aşağıda). Burada TEKRARLANMAZ: aynı işi yapan iki düğme,
              zincirdeki *"iki özdeş basamak adı"* bulgusunun kardeşidir. */}
          {taslakVar && sablon.yetkiler.duzenleyebilir && (
            <Button
              data-testid="sablon-yayinla"
              disabled={bosSablon}
              loading={yayinla.isPending}
              onClick={() => yayinla.mutate({ versionId: sablon.taslakSurumId! })}
            >
              Yayınla
            </Button>
          )}
        </Group>
      </Group>

      {/* KÜNYE (143): ad · kapsam · aksiyon kaynağı ARTIK BURADA. Kütüphane
          nüshasında ad ürün verisidir ve kilitli metindir (Ş5 — §19/9). */}
      <Card withBorder data-testid="sablon-kunye">
        <Group align="flex-end" gap="md" wrap="wrap">
          {sablon.kutuphaneden ? (
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                Şablon adı
              </Text>
              <Text size="sm" data-testid="sablon-ad-kilitli">
                {sablon.ad} (ad üründen gelir)
              </Text>
            </Stack>
          ) : (
            <TextInput
              w={280}
              label="Şablon adı"
              value={ad}
              disabled={!sablon.yetkiler.duzenleyebilir}
              onChange={(event) => setAd(event.currentTarget.value)}
              onBlur={() => {
                const yeni = ad.trim();
                if (!yeni || yeni === sablon.ad) {
                  setAd(sablon.ad);
                  return;
                }
                adYaz.mutate({ templateId: sablon.id, ad: yeni });
              }}
              data-testid="sablon-ad"
            />
          )}
          <Stack gap={2}>
            <Text size="xs" c="dimmed">
              Kapsam
            </Text>
            {/* ✏️ Y5/D2 — "matbu"/"merkezî" kavramı KALKTI (T1): kullanıcı
                artık bir DÜZEY değil, bir İŞYERİ KÜMESİ okur. */}
            <Text size="sm" data-testid="sablon-kapsam">
              {kapsamEtiketi(sablon)}
            </Text>
          </Stack>
          {sablon.kaynakTemplateAd && (
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                Köken
              </Text>
              <Text size="sm" data-testid="sablon-koken">
                {sablon.kaynakTemplateAd}’den çoğaltıldı
              </Text>
            </Stack>
          )}
          {sablon.kutuphaneden ? (
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                Aksiyon kaynağı
              </Text>
              <Text size="sm">Üründen gelir</Text>
            </Stack>
          ) : (
            <Select
              w={260}
              label="Aksiyon kaynağı"
              description="Kayıttan aksiyon açılacaksa sözlükten seçin"
              data={(aksiyonKaynaklari.data ?? []).map((k) => ({ value: k.id, label: k.ad }))}
              value={sablon.aksiyonKaynagiId}
              clearable
              disabled={!sablon.yetkiler.duzenleyebilir}
              onChange={(value) =>
                kaynakYaz.mutate({ templateId: sablon.id, aksiyonKaynagiId: value })
              }
              data-testid="sablon-aksiyon-kaynagi"
            />
          )}
        </Group>
      </Card>

      <KapsamKarti sablon={sablon} />

      <SurumGecmisi sablon={sablon} />

      {(adYaz.isError || kaynakYaz.isError || surumAc.isError || yayinla.isError) && (
        <Alert color={ANLAM.engel} role="alert" data-testid="sablon-hata">
          {(adYaz.error ?? kaynakYaz.error ?? surumAc.error ?? yayinla.error)?.message}
        </Alert>
      )}
      {bosSablon && taslakVar && (
        <Alert color={ANLAM.dikkat} data-testid="sablon-bos-uyari">
          Boş şablon yayınlanamaz — aşağıdaki soluk satırdan en az bir madde ekleyin.
        </Alert>
      )}
      {/* ⚠️ **G4 — BU BİR HATA DEĞİL, BİR DURUM AÇIKLAMASIDIR.** Ürün sahibi
          bandı hata sandı: metin bir iç belgeye atıf yapıyordu (*"§22.5/3"*)
          ve **ne yapılacağını söylemiyordu.** İki düzeltme: dil BİLGİ dilidir
          (D2 — kullanıcı ne yapacağını duyar) ve **çare bandın İÇİNDEDİR** —
          açıklama ile düğme arasında göz gezdirmek gerekmez. */}
      {!taslakVar && (
        <Alert color={ANLAM.bilgi} data-testid="sablon-yayin-kilidi-notu">
          <Group justify="space-between" align="center" wrap="wrap" gap="sm">
            <Text size="sm">
              Bu sürüm yayında ve değişmez. Değişiklik için yeni sürüm açın; aşağıdaki liste o
              zamana kadar salt okunur.
            </Text>
            {sablon.yetkiler.duzenleyebilir && (
              <Button
                size="xs"
                data-testid="sablon-taslak-ac"
                loading={surumAc.isPending}
                onClick={() => surumAc.mutate({ templateId: sablon.id })}
              >
                Düzenlemeye başla (yeni sürüm)
              </Button>
            )}
          </Group>
        </Alert>
      )}

      <QueryStatus queries={[surum]} />
      {surum.isSuccess &&
        (sema === null ? (
          <Alert color={ANLAM.engel} role="alert">
            Şablon sürümü okunamadı.
          </Alert>
        ) : (
          <SablonDuzenleyici
            sema={sema}
            duzenlenebilir={duzenlenebilir}
            kaydediliyor={surumGuncelle.isPending}
            onSema={(yeni) =>
              surumGuncelle.mutate({ versionId: sablon.taslakSurumId!, sema: yeni })
            }
          />
        ))}
      {surumGuncelle.isError && (
        <Alert color={ANLAM.engel} role="alert" data-testid="sablon-sema-hata">
          {surumGuncelle.error.message}
        </Alert>
      )}
    </Stack>
  );
}
